import React from 'react';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import NumericInput from './NumericInput';

const Input = ({
  label,
  type = 'text',
  /** When type="number": 'integer' = digits only; 'decimal' = optional minus + decimal separator */
  numericMode,
  value,
  onChange,
  placeholder,
  error,
  disabled = false,
  required = false,
  className = '',
  inputClassName = '',
  ...props
}) => {
  const inputClasses = `
    input-field
    min-h-[44px]
    ${error ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}
    ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}
    ${inputClassName}
  `.trim().replace(/\s+/g, ' ');

  const isNumberType = type === 'number';
  const numberFieldProps = isNumberType
    ? (() => {
        // min/max/step are for native type="number" only; omit from DOM for text numeric field
        // eslint-disable-next-line no-unused-vars -- strip on purpose
        const { min, max, step, ...rest } = props;
        return rest;
      })()
    : props;

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        {isNumberType ? (
          <NumericInput
            variant={numericMode === 'integer' ? 'integer' : 'decimal'}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            disabled={disabled}
            className={inputClasses}
            {...numberFieldProps}
          />
        ) : (
        <input
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          className={inputClasses}
          {...props}
        />
        )}
        {error && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
          </div>
        )}
      </div>
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

// Textarea component
export const Textarea = ({
  label,
  value,
  onChange,
  placeholder,
  error,
  disabled = false,
  required = false,
  rows = 3,
  className = '',
  ...props
}) => {
  const textareaClasses = `
    input-field
    ${error ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}
    ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}
  `.trim().replace(/\s+/g, ' ');

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <textarea
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        rows={rows}
        className={textareaClasses}
        {...props}
      />
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

// Select component
export const Select = ({
  label,
  value,
  onChange,
  options = [],
  placeholder = 'Select an option',
  error,
  disabled = false,
  required = false,
  className = '',
  ...props
}) => {
  const selectClasses = `
    input-field
    ${error ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}
    ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}
  `.trim().replace(/\s+/g, ' ');

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <select
        value={value}
        onChange={onChange}
        disabled={disabled}
        className={selectClasses}
        {...props}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value} title={option.label}>
            {option.label}
          </option>
        ))}
      </select>
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default Input;